import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';
import { Box, GreenButton, Input, RedButton, Textarea } from '~Styles';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';

const Wrapper = styled(Box)<{ width: string }>`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  width: ${({ width }) => width};
  margin-bottom: 25%;
  box-sizing: border-box;
  max-width: 500px;

  ${handyBreakpoint} {
    margin-bottom: auto;
    margin-top: 16px;
  }
`;

const ButtonWrapper = styled(Box)`
  flex-flow: row nowrap;
`;

interface Props {
  type?: string;
  onSubmit: (message: string) => void;
  onInput?: (message: string) => void;
  value?: string;
  placeHolder?: string;
  autoFocus?: boolean;
  width?: string;
  acceptEmpty?: boolean;
}

const MessageInput: React.FunctionComponent<Props> = ({
  type,
  onSubmit,
  onInput,
  value = '',
  placeHolder = '',
  autoFocus = true,
  width = '100%',
  acceptEmpty = false,
}) => {
  const [message, setMessage] = useState(value);
  const { i18n } = useI18n();

  useEffect(() => {
    setMessage(value);
  }, [value]);

  const handleClear = () => {
    if (!message.length) return;

    setMessage('');
    onInput && onInput('');
  };

  const buttonDisabled = acceptEmpty ? !acceptEmpty : !message.length;

  const clearButton = useButton({
    onClick: handleClear,
  });

  const saveButton = useButton({
    onClick: () => {
      onSubmit(message);
    },
    disabled: buttonDisabled,
  });

  return (
    <Wrapper
      display="flex"
      flexDirection="column"
      gap="xs"
      padding="small"
      width={width}
      onClick={(e) => {
        e.stopPropagation();
      }}>
      {type === 'text' ? (
        <Input
          dark
          type="text"
          autoFocus={autoFocus}
          value={message}
          placeholder={placeHolder}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setMessage(event.target.value);
            onInput && onInput(event.target.value);
          }}
        />
      ) : (
        <Textarea
          dark
          autoFocus={autoFocus}
          value={message}
          placeholder={placeHolder}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(event.target.value);
            onInput && onInput(event.target.value);
          }}
        />
      )}

      <ButtonWrapper display="flex" gap="xs" justifyContent="space-between">
        <RedButton {...clearButton} disabled={!message.length}>
          {i18n('General.Clear')}
        </RedButton>

        <GreenButton
          {...saveButton}
          style={{
            flex: 2,
          }}>
          {i18n('General.Ok')}
        </GreenButton>
      </ButtonWrapper>
    </Wrapper>
  );
};

export default MessageInput;
